import React, {
	useState,
	useEffect,
	useMemo,
	useCallback,
	lazy,
	Suspense,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { fetchQuestions } from "../services/questions";
import { fetchQuiz } from "../services/quizzes";
import SafeScreen from "components/shared/SafeScreen";
import Loader from "components/shared/Loader";

const QuizWrapper = lazy(() =>
	import("../components/quizBackgrounds/QuizWrapper")
);
const Question = lazy(() => import("../components/quiz/Question"));
const EndQuiz = lazy(() => import("../components/quiz/EndQuiz"));
const QuizIntroduction = lazy(() => import("components/quiz/QuizIntroduction"));

const QuizPage = () => {
	const { quizId } = useParams();
	const navigate = useNavigate();

	const player = useMemo(() => {
		try {
			return JSON.parse(localStorage.getItem("player") || "{}");
		} catch {
			return {};
		}
	}, []);

	const quizWrapper = useMemo(() => {
		try {
			return JSON.parse(localStorage.getItem("quizWrapper") || "{}");
		} catch {
			return {};
		}
	}, []);

	const [quiz, setQuiz] = useState(() => {
		try {
			return JSON.parse(localStorage.getItem("quiz") || "null");
		} catch {
			return null;
		}
	});

	const [questions, setQuestions] = useState([]);
	const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
	const [isLoading, setIsLoading] = useState(true);
	const [isQuizEnd, setIsQuizEnd] = useState(false);
	const [answeredQuestions, setAnsweredQuestions] = useState(new Set());
	const [showQuizIntroduction, setShowQuizIntroduction] = useState(true);
	const [triggerQuestionAnimation, setTriggerQuestionAnimation] =
		useState(false);

	const currentQuestion = questions[currentQuestionIndex] || null;
	const totalQuestions = questions.length;

	const [quizResults, setQuizResults] = useState({
		quiz_id: quizId,
		player_id: player.id,
		score: 0,
		responses: {},
	});

	useEffect(() => {
		if (!player.id) navigate(`/start/${quizId}`);
	}, [player, quizId, navigate]);

	useEffect(() => {
		if (!quiz) {
			fetchQuiz(quizId)
				.then(({ data, error }) => {
					if (error) throw new Error("Erreur lors du chargement du quiz.");
					setQuiz(data);
					localStorage.setItem("quiz", JSON.stringify(data));
				})
				.catch((error) => toast.error(error.message));
		}
	}, [quiz, quizId]);

	useEffect(() => {
		fetchQuestions(quizId)
			.then((data) => {
				if (!data || data.length === 0) {
					throw new Error("No questions available for this quiz.");
				}
				setQuestions(data);
			})
			.catch((error) => toast.error(error.message))
			.finally(() => setIsLoading(false));
	}, [quizId]);

	const handleAnswer = useCallback(
		(answerValue) => {
			if (!currentQuestion) return;
			const questionId = currentQuestion.id;
			let _addedScore = 0;

			const getSelectedOption = (val) =>
				currentQuestion.options.find((option) => option.value === val);

			if (
				["single-choice", "iserba-situation-multi-choice"].includes(
					currentQuestion.type
				)
			) {
				if (getSelectedOption(answerValue)?.isCorrect) {
					_addedScore += currentQuestion.points;
				}
			} else if (currentQuestion.type === "multiple-choice") {
				const correctAnswers = currentQuestion.options
					.filter((option) => option.isCorrect)
					.map((option) => option.value);
				if (
					Array.isArray(answerValue) &&
					answerValue.every((v) => correctAnswers.includes(v)) &&
					correctAnswers.every((v) => answerValue.includes(v))
				) {
					_addedScore += currentQuestion.points;
				}
			} else {
				_addedScore += currentQuestion.points;
			}

			setQuizResults((prev) => ({
				...prev,
				responses: { ...prev.responses, [questionId]: answerValue },
				score: prev.score + _addedScore,
			}));
		},
		[currentQuestion]
	);

	const handleNext = useCallback(() => {
		if (!currentQuestion) return;
		const questionId = currentQuestion.id;

		if (currentQuestionIndex < totalQuestions - 1) {
			if (!answeredQuestions.has(questionId)) {
				setTriggerQuestionAnimation({ currentQuestionIndex, totalQuestions });
				setAnsweredQuestions((prev) => new Set(prev).add(questionId));
			}
			setCurrentQuestionIndex((prev) => prev + 1);
		} else {
			setTriggerQuestionAnimation({ currentQuestionIndex, totalQuestions });
			setIsQuizEnd(true);
		}
	}, [
		currentQuestion,
		currentQuestionIndex,
		totalQuestions,
		answeredQuestions,
	]);

	const handlePrevious = useCallback(() => {
		if (isQuizEnd) return setIsQuizEnd(false);
		if (currentQuestionIndex > 0) setCurrentQuestionIndex((prev) => prev - 1);
	}, [isQuizEnd, currentQuestionIndex]);

	return (
		<SafeScreen className="flex flex-1">
			<Suspense fallback={<Loader />}>
				<QuizWrapper
					quizWrapper={quizWrapper}
					quizOrganizationId={quiz?.organization_id}
					triggerQuestionAnimation={triggerQuestionAnimation}
				/>

				{isLoading || !quiz ? (
					<div className="flex items-center justify-center h-full w-full">
						<Loader />
					</div>
				) : showQuizIntroduction && quiz?.config?.show_quiz_introduction ? (
					<div className="flex flex-col h-full w-full z-10 bg-transparent">
						<QuizIntroduction
							handleClose={() => setShowQuizIntroduction(false)}
							quiz={quiz}
						/>
					</div>
				) : (
					<div className="flex flex-col w-full h-full z-10">
						{isQuizEnd ? (
							<div className="flex items-center justify-center w-full h-full">
								<EndQuiz
									player={player}
									organizationId={quiz.organization_id}
									quizId={quizId}
									quizResults={quizResults}
									questions={questions}
									onPrevious={handlePrevious}
								/>
							</div>
						) : (
							currentQuestion && (
								<Question
									quiz={quiz}
									question={currentQuestion}
									currentQuestionIndex={currentQuestionIndex}
									totalQuestions={totalQuestions}
									quizResults={quizResults}
									setQuizResults={setQuizResults}
									onAnswer={handleAnswer}
									defaultValue={
										quizResults.responses[currentQuestion.id]?.value
									}
									handleNext={handleNext}
								/>
							)
						)}
					</div>
				)}
			</Suspense>
		</SafeScreen>
	);
};

export default QuizPage;
